import { useState, type FormEvent } from 'react';
import { Lock, Pencil, Plus, SlidersHorizontal, Sparkles, Trash2 } from 'lucide-react';
import { errorMessage, useLocale, type MessageKey } from '../../i18n';
import { ConfirmButton } from '../ui/ConfirmButton';
import {
  areasFor,
  levelCaps,
  levelStrength,
  standingIn,
  toneOf,
  withLevel,
  type AreaLevel,
} from '../platform/roleAreas';
import {
  createOwnRole,
  deleteOwnRole,
  renameOwnRole,
  setOwnRoleCapabilities,
  type AccessRole,
  type AccessWriteResult,
} from '../../lib/api/organizationAccess';

// ─────────────────────────────────────────────────────────────────────
// EGNE ROLLER — the organization's own roles, on its «Tilganger» page
// (2026-11-01; the server half is #1215, ROLES_AND_ACCESS §4l row 5).
//
// An access manager lists, defines, reshapes, renames and deletes the roles
// the organization keeps for itself. The rights are drawn the way /sa draws
// them — by fagområde, with the same named levels (`roleAreas.ts`) — but
// only over what `ownRoles.offerable` says a write from THIS person would
// accept: the organization's ceiling, never a reserved name, nothing beyond
// the person (2B). A right the role carries that the person may not touch is
// shown, checked and locked, and kept on save.
//
// It decides nothing. Every write is the server's to refuse, and a refusal is
// shown as what it is — a code, made a sentence here, with the rights it
// names. The list is re-read after every write; the page never edits its copy.
// ─────────────────────────────────────────────────────────────────────

export interface OwnRolesSectionProps {
  /** Every role the page lists; the organization's own are picked out here. */
  roles: AccessRole[];
  /** What an own role may carry when this person writes it. */
  offerable: string[];
  /** How many people hold a role, from the page's people list. */
  holdersOf: (roleId: string) => number;
  /** A write finished: the sentence to show, and whether to re-read the list. */
  onSettled: (notice: { kind: 'ok' | 'error'; text: string }, reload: boolean) => void;
  /** Injectable for tests. */
  create?: typeof createOwnRole;
  rename?: typeof renameOwnRole;
  setCapabilities?: typeof setOwnRoleCapabilities;
  remove?: typeof deleteOwnRole;
}

/** The refusals this section has its own sentence for; anything else goes to `errorMessage`. */
const OWN_ROLE_REFUSAL: Record<string, MessageKey> = {
  role_in_use: 'access.ownRoles.error.role_in_use',
  beyond_organization: 'access.ownRoles.error.beyond_organization',
  reserved_capability: 'access.ownRoles.error.reserved_capability',
  beyond_own_authority: 'access.ownRoles.error.beyond_own_authority',
  last_holder: 'access.ownRoles.error.last_holder',
  name_is_system_role: 'access.ownRoles.error.name_is_system_role',
  name_has_no_usable_key: 'access.ownRoles.error.name_has_no_usable_key',
  not_found: 'access.ownRoles.error.not_found',
};

const DOTS = { 1: '●○○', 2: '●●○', 3: '●●●' } as const;

type Editing =
  | { roleId: string; mode: 'rights'; caps: string[] }
  | { roleId: string; mode: 'name'; name: string; description: string }
  | null;

export function OwnRolesSection({
  roles,
  offerable,
  holdersOf,
  onSettled,
  create = createOwnRole,
  rename = renameOwnRole,
  setCapabilities = setOwnRoleCapabilities,
  remove = deleteOwnRole,
}: OwnRolesSectionProps) {
  const { t } = useLocale();
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState<{ name: string; description: string; caps: string[] } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);

  const own = roles.filter((r) => r.own);

  /** A right's short name, from the catalog; the key itself if the catalog has none. */
  const capName = (cap: string): string => {
    const key = `capability.${cap}` as MessageKey;
    const name = t(key);
    return name === key ? cap : name;
  };

  const settle = (outcome: AccessWriteResult, done: string): boolean => {
    if (outcome.state === 'ok') {
      onSettled({ kind: 'ok', text: done }, true);
      return true;
    }
    if (outcome.state === 'forbidden') {
      onSettled({ kind: 'error', text: t('access.forbidden') }, false);
      return false;
    }
    if (outcome.state === 'refused') {
      const key = OWN_ROLE_REFUSAL[outcome.reason];
      const text = key
        ? t(key, {
            capabilities: outcome.missing.map(capName).join(', '),
            n: outcome.holders ?? 0,
          })
        : errorMessage(t, outcome.reason);
      // A role that vanished, or one somebody now holds, means the list is
      // stale: re-read it so the page shows what the server just said.
      onSettled(
        { kind: 'error', text },
        outcome.reason === 'not_found' || outcome.reason === 'role_in_use',
      );
      return false;
    }
    onSettled({ kind: 'error', text: t('errors.unknown') }, false);
    return false;
  };

  const run = async (write: () => Promise<AccessWriteResult>, done: string) => {
    if (busy) return false;
    setBusy(true);
    const ok = settle(await write(), done);
    setBusy(false);
    return ok;
  };

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    const name = draft.name.trim();
    const ok = await run(
      () =>
        create({
          name,
          description: draft.description.trim() || null,
          capabilities: draft.caps,
        }),
      t('access.ownRoles.created', { role: name }),
    );
    if (ok) setDraft(null);
  };

  const saveRights = async (role: AccessRole) => {
    if (editing?.mode !== 'rights') return;
    const ok = await run(
      () => setCapabilities(role.id, editing.caps),
      t('access.ownRoles.saved', { role: role.name }),
    );
    if (ok) setEditing(null);
  };

  const saveName = async (e: FormEvent, role: AccessRole) => {
    e.preventDefault();
    if (editing?.mode !== 'name') return;
    const name = editing.name.trim();
    const ok = await run(
      () => rename(role.id, { name, description: editing.description.trim() || null }),
      t('access.ownRoles.renamed', { role: name }),
    );
    if (ok) setEditing(null);
  };

  const doDelete = (role: AccessRole) =>
    void run(() => remove(role.id), t('access.ownRoles.deleted', { role: role.name }));

  return (
    <section className="card p-5" data-testid="own-roles">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
            <Sparkles size={18} /> {t('access.ownRoles.title')}
          </h2>
          <p className="mt-1 text-xs text-ink-3">{t('access.ownRoles.lead')}</p>
        </div>
        {!draft && (
          <button
            type="button"
            className="btn-outline text-xs inline-flex items-center gap-1"
            onClick={() => {
              setEditing(null);
              setDraft({ name: '', description: '', caps: [] });
            }}
            data-testid="own-roles-new"
          >
            <Plus size={12} /> {t('access.ownRoles.new')}
          </button>
        )}
      </div>

      {draft && (
        <form
          onSubmit={(e) => void submitCreate(e)}
          className="mt-3 rounded-lg border border-line p-3 space-y-3"
          data-testid="own-roles-create"
        >
          <NameFields
            name={draft.name}
            description={draft.description}
            onName={(name) => setDraft({ ...draft, name })}
            onDescription={(description) => setDraft({ ...draft, description })}
            idPrefix="own-roles-create"
          />
          <CapabilityPicker
            value={draft.caps}
            offerable={offerable}
            onChange={(caps) => setDraft({ ...draft, caps })}
            disabled={busy}
            idPrefix="own-roles-create"
            capName={capName}
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className="btn-lime text-xs"
              disabled={busy || !draft.name.trim()}
              data-testid="own-roles-create-submit"
            >
              {busy ? t('access.ownRoles.saving') : t('access.ownRoles.create')}
            </button>
            <button type="button" className="btn-outline text-xs" onClick={() => setDraft(null)}>
              {t('access.ownRoles.cancel')}
            </button>
          </div>
        </form>
      )}

      {own.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3" data-testid="own-roles-none">
          {t('access.ownRoles.none')}
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {own.map((role) => {
            const caps = role.capabilities ?? [];
            const holders = holdersOf(role.id);
            const locked = role.beyondYou.length > 0;
            const open = editing?.roleId === role.id ? editing : null;
            return (
              <li
                key={role.id}
                className="rounded-lg border border-line px-3 py-2"
                data-testid={`own-role-${role.key}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-ink" lang="nb" title={role.key}>
                      {role.name}
                    </span>
                    <span className="ml-2 text-[11px] text-ink-3">
                      {caps.length === 0
                        ? t('access.ownRoles.noRights')
                        : t('access.ownRoles.rights', { n: caps.length })}
                      {holders > 0 && ` · ${t('access.ownRoles.holders', { n: holders })}`}
                    </span>
                    {role.description && (
                      <p className="mt-0.5 text-xs text-ink-3">{role.description}</p>
                    )}
                  </div>
                  {locked ? (
                    // 2B: a role carrying rights this person lacks is not theirs
                    // to reshape, rename or delete — the server refuses all three.
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-3"
                      title={t('access.beyondYou.help', {
                        capabilities: role.beyondYou.map(capName).join(', '),
                      })}
                      data-testid={`own-role-locked-${role.key}`}
                    >
                      <Lock size={10} /> {t('access.beyondYou')}
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        className="btn-outline text-xs inline-flex items-center gap-1"
                        aria-expanded={open?.mode === 'rights'}
                        onClick={() =>
                          setEditing(
                            open?.mode === 'rights'
                              ? null
                              : { roleId: role.id, mode: 'rights', caps: [...caps] },
                          )
                        }
                        data-testid={`own-role-edit-${role.key}`}
                      >
                        <SlidersHorizontal size={12} /> {t('access.ownRoles.editRights')}
                      </button>
                      <button
                        type="button"
                        className="btn-outline text-xs inline-flex items-center gap-1"
                        aria-expanded={open?.mode === 'name'}
                        onClick={() =>
                          setEditing(
                            open?.mode === 'name'
                              ? null
                              : {
                                  roleId: role.id,
                                  mode: 'name',
                                  name: role.name,
                                  description: role.description ?? '',
                                },
                          )
                        }
                        data-testid={`own-role-rename-${role.key}`}
                      >
                        <Pencil size={12} /> {t('access.ownRoles.rename')}
                      </button>
                      <ConfirmButton
                        question={t('access.ownRoles.deleteQuestion', { role: role.name })}
                        confirmLabel={t('access.ownRoles.delete')}
                        onConfirm={() => doDelete(role)}
                        className="btn-outline text-xs inline-flex items-center gap-1 hover:text-rose"
                        title={holders > 0 ? t('access.ownRoles.inUse') : undefined}
                        disabled={busy || holders > 0}
                      >
                        <Trash2 size={12} /> {t('access.ownRoles.delete')}
                      </ConfirmButton>
                    </div>
                  )}
                </div>
                {!locked && holders > 0 && (
                  <p
                    className="mt-1 text-[11px] text-ink-3"
                    data-testid={`own-role-in-use-${role.key}`}
                  >
                    {t('access.ownRoles.inUse')}
                  </p>
                )}

                {open?.mode === 'name' && (
                  <form
                    onSubmit={(e) => void saveName(e, role)}
                    className="mt-2 space-y-2"
                    data-testid={`own-role-rename-form-${role.key}`}
                  >
                    <NameFields
                      name={open.name}
                      description={open.description}
                      onName={(name) => setEditing({ ...open, name })}
                      onDescription={(description) => setEditing({ ...open, description })}
                      idPrefix={`own-role-${role.id}`}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="submit"
                        className="btn-lime text-xs"
                        disabled={busy || !open.name.trim()}
                        data-testid={`own-role-rename-save-${role.key}`}
                      >
                        {busy ? t('access.ownRoles.saving') : t('access.ownRoles.saveName')}
                      </button>
                      <button
                        type="button"
                        className="btn-outline text-xs"
                        onClick={() => setEditing(null)}
                      >
                        {t('access.ownRoles.cancel')}
                      </button>
                    </div>
                  </form>
                )}

                {open?.mode === 'rights' && (
                  <div className="mt-2 space-y-2" data-testid={`own-role-rights-${role.key}`}>
                    <CapabilityPicker
                      value={open.caps}
                      offerable={offerable}
                      onChange={(next) => setEditing({ ...open, caps: next })}
                      disabled={busy}
                      idPrefix={`own-role-${role.id}`}
                      capName={capName}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="btn-lime text-xs"
                        disabled={busy || sameSet(open.caps, caps)}
                        onClick={() => void saveRights(role)}
                        data-testid={`own-role-save-${role.key}`}
                      >
                        {busy ? t('access.ownRoles.saving') : t('access.ownRoles.saveRights')}
                      </button>
                      <button
                        type="button"
                        className="btn-outline text-xs"
                        onClick={() => setEditing(null)}
                      >
                        {t('access.ownRoles.cancel')}
                      </button>
                      {!sameSet(open.caps, caps) && (
                        <span className="text-[11px] text-ink-3">
                          {t('access.ownRoles.pending', {
                            added: open.caps.filter((c) => !caps.includes(c)).length,
                            removed: caps.filter((c) => !open.caps.includes(c)).length,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

function NameFields({
  name,
  description,
  onName,
  onDescription,
  idPrefix,
}: {
  name: string;
  description: string;
  onName: (v: string) => void;
  onDescription: (v: string) => void;
  idPrefix: string;
}) {
  const { t } = useLocale();
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <label className="block">
        <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
          {t('access.ownRoles.name')}
        </span>
        <input
          className="input mt-1 w-full"
          value={name}
          maxLength={80}
          onChange={(e) => onName(e.target.value)}
          data-testid={`${idPrefix}-name`}
        />
      </label>
      <label className="block">
        <span className="font-mono uppercase tracking-wider text-xs text-ink-3">
          {t('access.ownRoles.description')}
        </span>
        <input
          className="input mt-1 w-full"
          value={description}
          maxLength={200}
          onChange={(e) => onDescription(e.target.value)}
          data-testid={`${idPrefix}-description`}
        />
      </label>
    </div>
  );
}

/**
 * THE RIGHTS, BY FAGOMRÅDE — /sa's areas and levels over what this person may
 * offer. Choosing a level replaces that area's OFFERED rights with the level's
 * (`withLevel`); a right outside the offer stays as it is, shown locked.
 */
function CapabilityPicker({
  value,
  offerable,
  onChange,
  disabled,
  idPrefix,
  capName,
}: {
  value: string[];
  offerable: string[];
  onChange: (next: string[]) => void;
  disabled: boolean;
  idPrefix: string;
  capName: (cap: string) => string;
}) {
  const { t } = useLocale();
  const offered = new Set(offerable);
  // Everything the role holds is drawn, offered or not, so nothing it carries
  // is invisible; only the offered rights can be changed.
  const shown = [...new Set([...offerable, ...value])];
  const areas = areasFor(shown);
  if (offerable.length === 0 && value.length === 0) {
    return <p className="text-xs text-ink-3">{t('access.ownRoles.nothingOffered')}</p>;
  }
  const toggle = (cap: string) =>
    onChange(value.includes(cap) ? value.filter((c) => c !== cap) : [...value, cap]);

  return (
    <div className="space-y-2" data-testid={`${idPrefix}-picker`}>
      {areas.map(({ area, caps }) => {
        const tone = toneOf(area);
        const areaOffered = caps.filter((c) => offered.has(c));
        const standing = standingIn(area, areaOffered, value);
        // A level is offered once, at its lowest name, and only when it names
        // something this person may give.
        const seen = new Set<string>();
        const levels = area.levels.filter((level: AreaLevel) => {
          const set = levelCaps(level, areaOffered).sort().join(',');
          if (!set || seen.has(set)) return false;
          seen.add(set);
          return true;
        });
        return (
          <fieldset
            key={area.key}
            className={`rounded-lg border-l-4 px-3 py-2 ${tone.panel}`}
            disabled={disabled}
            data-testid={`${idPrefix}-area-${area.key}`}
          >
            <legend className={`font-display text-sm font-semibold ${tone.head}`}>
              {t(area.label)}
            </legend>
            {areaOffered.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {[null, ...levels].map((level) => {
                  const key = level?.key ?? 'none';
                  const active =
                    level === null
                      ? standing.kind === 'none'
                      : standing.kind === 'level' && standing.level.key === level.key;
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => onChange(withLevel(value, areaOffered, level))}
                      className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                        active
                          ? 'bg-ink text-white border-ink'
                          : 'border-line bg-paper text-ink-2 hover:bg-bg-soft'
                      }`}
                      data-testid={`${idPrefix}-level-${area.key}-${key}`}
                    >
                      {level ? (
                        <>
                          <span aria-hidden className="mr-1 tracking-tighter">
                            {DOTS[levelStrength(area, level)]}
                          </span>
                          {t(level.label)}
                        </>
                      ) : (
                        t('saAreas.none')
                      )}
                    </button>
                  );
                })}
                {standing.kind === 'custom' && (
                  <span className={`rounded-full px-3 py-1 text-xs bg-paper ${tone.custom}`}>
                    {t('saAreas.custom', {
                      held: String(standing.held),
                      total: String(standing.total),
                    })}
                  </span>
                )}
              </div>
            )}
            <ul className="mt-2 grid gap-1 sm:grid-cols-2">
              {caps.map((cap) => {
                const mayChange = offered.has(cap);
                const id = `${idPrefix}-cap-${cap}`;
                return (
                  <li key={cap} className="flex items-start gap-2 rounded-lg px-2 py-1 bg-paper">
                    <input
                      id={id}
                      type="checkbox"
                      className="mt-0.5"
                      checked={value.includes(cap)}
                      disabled={disabled || !mayChange}
                      onChange={() => toggle(cap)}
                      data-testid={id}
                    />
                    <label
                      htmlFor={id}
                      className={`text-xs leading-snug ${mayChange ? 'text-ink' : 'text-ink-3'}`}
                      title={mayChange ? cap : `${cap} — ${t('access.ownRoles.notYours')}`}
                    >
                      {capName(cap)}
                      {!mayChange && <Lock size={10} className="inline ml-1" aria-hidden />}
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        );
      })}
    </div>
  );
}
